/**
 * URL Utilities for SEO-friendly routing
 * 
 * Provides functions to generate and parse SEO-friendly URLs
 * for the Collegiate NFL Impact application.
 */

import { CURRENT_YEAR } from '../constants/app';
import { isPositionPathToken } from './positionPathPages';
import {
  getRookiesPath,
  isPlayersWave2PositionToken,
  matchesPlayersWave2Position,
} from './playersPathPages';
import { getPlayerCareerPath, parsePlayerCareerPath } from './playerCareer';

export const BASE_SITE_URL = 'https://collegiatenflimpact.com';

/**
 * Valid stat filter categories that map to URL segments
 */
export const STAT_CATEGORIES = [
  '',
  'passing',
  'rushing',
  'receiving',
  'defense',
  'kicking',
  'punting',
  'returning',
  'scoring',
] as const;

export type StatCategory = typeof STAT_CATEGORIES[number];

/** Player-leaderboard views. Rookie paths are year-qualified, not query filters. */
export type PlayersView = 'leaders' | 'rookies' | 'rookies-by-college';

/**
 * Validates if a string is a valid stat category
 */
export const isValidStatCategory = (category: string | undefined | null): category is StatCategory => {
  if (!category) return true; // Empty string is valid (Fantasy Points)
  return STAT_CATEGORIES.includes(category as StatCategory);
};

/**
 * Generates SEO-friendly URL for rankings page
 * @param statCategory - The stat category (passing, rushing, etc.) or empty string for Fantasy Points
 * @param year - Optional year (if provided, includes year in URL)
 * @param week - Optional week number (if provided, includes week in URL; requires year)
 * @param yearAggregate - Whether to show year aggregate (default: true). If false and year is provided, week must also be provided.
 * @param conferenceView - Whether to show conference view (default: false). If true, adds /conference to the URL.
 * @param conferenceAbbr - Optional conference abbreviation (e.g., "acc"). If provided, adds /conference/:abbr to the URL.
 * @param position - Optional position path token (e.g., "wr"). College overall view only; after year when year is set.
 * @returns URL path string (e.g., '/rankings/passing', '/rankings/receiving/wr', '/rankings/receiving/2025/wr')
 */
export const getRankingsUrl = (
  statCategory: StatCategory = '',
  year?: number,
  week?: number,
  yearAggregate: boolean = true,
  conferenceView: boolean = false,
  conferenceAbbr?: string | null,
  position?: string | null
): string => {
  let url = '/rankings';
  
  if (statCategory) {
    url += `/${statCategory}`;
  }
  
  // Add year if provided
  if (year !== undefined) {
    url += `/${year}`;
    
    // Add week if provided and not year aggregate
    if (!yearAggregate && week !== undefined) {
      url += `/week/${week}`;
    }
  }
  
  // Add conference suffix if conference view is enabled
  if (conferenceView) {
    url += '/conference';
    
    // Add conference abbreviation if provided (for conference team view)
    // Normalize to lowercase for consistent URLs and SEO
    if (conferenceAbbr) {
      url += `/${conferenceAbbr.toLowerCase()}`;
    }
  } else if (position && isPositionPathToken(position) && yearAggregate) {
    // Position path pages are college overall only (no conference / week)
    url += `/${position.toLowerCase()}`;
  }
  
  return url;
};

/**
 * Internal link target for a season-aggregate college rankings view: the yearless stat hub
 * for the current season (the hub shows current-season data), /rankings/{stat}/{year} otherwise.
 */
export const getRankingsSeasonLinkUrl = (
  statCategory: StatCategory = '',
  year: number = CURRENT_YEAR
): string => getRankingsUrl(statCategory, year === CURRENT_YEAR ? undefined : year);

/**
 * Parses URL parameters from rankings URL path
 * @param pathname - The URL pathname (e.g., '/rankings/passing/2025/week/5', '/rankings/passing/2025/conference', '/rankings/passing/2025/conference/acc')
 * @returns Object with statCategory, year, week, yearAggregate, conferenceView, and conferenceAbbr
 */
export const parseRankingsUrl = (pathname: string): {
  statCategory: StatCategory;
  year?: number;
  week?: number;
  yearAggregate: boolean;
  conferenceView: boolean;
  conferenceAbbr?: string | null;
  /** Path position token (e.g. wr), when present on college overall season URLs. */
  position?: string | null;
} => {
  const segments = pathname.split('/').filter(Boolean);
  
  // Default return value
  const result: {
    statCategory: StatCategory;
    year?: number;
    week?: number;
    yearAggregate: boolean;
    conferenceView: boolean;
    conferenceAbbr?: string | null;
    position?: string | null;
  } = {
    statCategory: '',
    yearAggregate: true,
    conferenceView: false,
    conferenceAbbr: null,
    position: null,
  };
  
  // Handle /rankings or /rankings/:category
  if (segments.length === 0 || segments[0] !== 'rankings') {
    return result;
  }
  
  // Just /rankings - Fantasy Points, no year
  if (segments.length === 1) {
    return result;
  }
  
  // Check if URL contains /conference
  const conferenceIndex = segments.indexOf('conference');
  const hasConference = conferenceIndex !== -1;
  
  // If conference is present, check if there's an abbreviation after it
  // Normalize to lowercase for consistent URLs and SEO
  if (hasConference && segments.length > conferenceIndex + 1) {
    result.conferenceAbbr = segments[conferenceIndex + 1].toLowerCase();
  }
  
  // Calculate effective length (excluding /conference and /:abbr if present)
  const effectiveLength = hasConference 
    ? (result.conferenceAbbr ? conferenceIndex : conferenceIndex + 1)
    : segments.length;
  
  // Check if second segment is a stat category or a year
  const secondSegment = segments[1];
  
  // Check if it's a valid stat category
  if (isValidStatCategory(secondSegment)) {
    result.statCategory = secondSegment;
    
    // Check if there's a year (third segment)
    if (effectiveLength >= 3) {
      const year = parseInt(segments[2], 10);
      if (!isNaN(year) && year >= 2010 && year <= 2100) {
        result.year = year;
        
        // Check if there's a week (fourth segment should be "week", fifth should be the week number)
        if (effectiveLength >= 5 && segments[3] === 'week') {
          const week = parseInt(segments[4], 10);
          if (!isNaN(week) && week >= 1 && week <= 25) {
            result.week = week;
            result.yearAggregate = false;
          }
        } else if (
          effectiveLength >= 4 &&
          result.yearAggregate &&
          !hasConference &&
          isPositionPathToken(segments[3])
        ) {
          // /rankings/{stat}/{year}/{pos}
          result.position = segments[3].toLowerCase();
        }
      } else if (
        effectiveLength >= 3 &&
        !hasConference &&
        isPositionPathToken(segments[2])
      ) {
        // /rankings/{stat}/{pos} (yearless hub)
        result.position = segments[2].toLowerCase();
      }
    }
  } else {
    // Second segment might be a year (for /rankings/2025 format - no stat category)
    const year = parseInt(secondSegment, 10);
    if (!isNaN(year) && year >= 2010 && year <= 2100) {
      result.year = year;
      
      // Check if there's a week
      if (effectiveLength >= 4 && segments[2] === 'week') {
        const week = parseInt(segments[3], 10);
        if (!isNaN(week) && week >= 1 && week <= 25) {
          result.week = week;
          result.yearAggregate = false;
        }
      }
    }
  }
  
  // Set conferenceView flag
  result.conferenceView = hasConference;
  
  return result;
};

/**
 * Parses stat category from URL path (backwards compatibility)
 * @param pathname - The URL pathname (e.g., '/rankings/passing')
 * @returns The stat category or empty string if not found/invalid
 * @deprecated Use parseRankingsUrl instead for full URL parsing
 */
export const parseStatCategoryFromUrl = (pathname: string): StatCategory => {
  return parseRankingsUrl(pathname).statCategory;
};

/**
 * Generates SEO-friendly URL for college detail page
 * @param slug - The college slug (e.g., 'auburn-tigers')
 * @param statFilter - The stat category filter (e.g., 'passing', 'rushing'). If undefined/empty, no statFilter in URL (Fantasy Points view)
 * @param year - Optional year (if provided, includes year in URL)
 * @param week - Optional week number (if provided, includes week in URL; requires year)
 * @param yearAggregate - Whether to show year aggregate (default: true). If false and year is provided, week must also be provided.
 * @returns URL path string (e.g., '/college/auburn-tigers/passing', '/college/auburn-tigers/passing/2025', '/college/auburn-tigers/2025' for Fantasy Points)
 */
/**
 * Preferred path for the address bar and internal links.
 * Uses /college/{slug} for the default-season Fantasy Points season view (SEO hub URL);
 * otherwise uses path segments /college/{slug}/{stat?}/{year}/….
 */
export const getCollegeHubOrDetailPath = (
  slug: string,
  statFilter: string | undefined,
  year: number,
  week: number | undefined,
  yearAggregate: boolean,
  defaultSeasonYear: number
): string => {
  const hasStat = Boolean(statFilter);
  if (!hasStat && yearAggregate && year === defaultSeasonYear) {
    return `/college/${slug}`;
  }
  return getCollegeUrl(
    slug,
    statFilter || undefined,
    year,
    yearAggregate ? undefined : week,
    yearAggregate
  );
};

/**
 * Generates SEO-friendly URL for college detail page (path segments, not query params).
 * @param slug - The college slug (e.g., 'auburn-tigers')
 * @param statFilter - The stat category filter. If undefined/empty, Fantasy Points view.
 * @param year - Optional year (if provided, includes year in URL)
 * @param week - Optional week number (requires year; yearAggregate false)
 * @param yearAggregate - Whether to show year aggregate (default: true)
 */
export const getCollegeUrl = (
  slug: string,
  statFilter?: string,
  year?: number,
  week?: number,
  yearAggregate: boolean = true
): string => {
  let url = `/college/${slug}`;

  if (statFilter) {
    url += `/${statFilter}`;
  }

  if (year !== undefined) {
    url += `/${year}`;

    if (!yearAggregate && week !== undefined) {
      url += `/week/${week}`;
    }
  }

  return url;
};

/**
 * Parses URL parameters from college URL path
 * @param pathname - The URL pathname (e.g., '/college/auburn-tigers/passing/2025/week/5')
 * @returns Object with slug, statFilter (optional), year, week, and yearAggregate, or null if not a college URL
 */
export const parseCollegeUrl = (pathname: string): {
  slug: string;
  statFilter?: string;
  year?: number;
  week?: number;
  yearAggregate: boolean;
} | null => {
  const segments = pathname.split('/').filter(Boolean);
  
  // Must start with 'college'
  if (segments.length === 0 || segments[0] !== 'college') {
    return null;
  }
  
  // Must have at least a slug (second segment)
  if (segments.length < 2) {
    return null;
  }
  
  const slug = segments[1];
  const result: {
    slug: string;
    statFilter?: string;
    year?: number;
    week?: number;
    yearAggregate: boolean;
  } = {
    slug,
    yearAggregate: true,
  };
  
  // Check if third segment is a statFilter (valid stat category) or a year
  if (segments.length >= 3) {
    const thirdSegment = segments[2];
    const year = parseInt(thirdSegment, 10);
    
    // Check if it's a valid stat category
    if (isValidStatCategory(thirdSegment)) {
      result.statFilter = thirdSegment;
      
      // Check if there's a year (fourth segment)
      if (segments.length >= 4) {
        const year = parseInt(segments[3], 10);
        if (!isNaN(year) && year >= 2010 && year <= 2100) {
          result.year = year;
          
          // Check if there's a week (fifth segment should be "week", sixth should be the week number)
          if (segments.length >= 6 && segments[4] === 'week') {
            const week = parseInt(segments[5], 10);
            if (!isNaN(week) && week >= 1 && week <= 25) {
              result.week = week;
              result.yearAggregate = false;
            }
          }
        }
      }
    } else if (!isNaN(year) && year >= 2010 && year <= 2100) {
      // Third segment is a year (Fantasy Points view, no statFilter)
      result.year = year;
      
      // Check if there's a week (fourth segment should be "week", fifth should be the week number)
      if (segments.length >= 5 && segments[3] === 'week') {
        const week = parseInt(segments[4], 10);
        if (!isNaN(week) && week >= 1 && week <= 25) {
          result.week = week;
          result.yearAggregate = false;
        }
      }
    }
  }
  
  return result;
};

/**
 * Generates SEO-friendly URL for player leaderboard pages.
 * Wave 2 yearless position paths: /players/{stat}/{pos} (wr|te|rb|qb when allowlisted).
 * @returns e.g. '/players', '/players/receiving', '/players/receiving/wr', '/players/receiving/2025'
 */
export const getPlayersUrl = (
  statCategory: StatCategory = '',
  year?: number,
  week?: number,
  yearAggregate: boolean = true,
  position?: string | null
): string => {
  let url = '/players';

  if (statCategory) {
    url += `/${statCategory}`;
  }

  if (year !== undefined) {
    url += `/${year}`;
    if (!yearAggregate && week !== undefined) {
      url += `/week/${week}`;
    }
  } else if (position) {
    url += `/${String(position).toLowerCase()}`;
  }

  return url;
};

/**
 * Yearless players hub for the current season; otherwise /players/{stat}/{year}.
 */
export const getPlayersSeasonLinkUrl = (
  statCategory: StatCategory = '',
  year: number = CURRENT_YEAR
): string => getPlayersUrl(statCategory, year === CURRENT_YEAR ? undefined : year);

/**
 * Rookie rankings path. Year is required; by-college is a path segment, not ?classes=.
 */
export const getRookiesUrl = (year: number, byCollege: boolean = false): string =>
  getRookiesPath(year, byCollege);

/**
 * Parses player leaderboard URL path segments.
 * After /players/{stat}/: numeric → year; wr|te|rb|qb → Wave 2 position; else ignored (invalid path).
 */
export const parsePlayersUrl = (pathname: string): {
  statCategory: StatCategory;
  year?: number;
  week?: number;
  yearAggregate: boolean;
  view: PlayersView;
  position?: string;
} => {
  const segments = pathname.split('/').filter(Boolean);
  const result: {
    statCategory: StatCategory;
    year?: number;
    week?: number;
    yearAggregate: boolean;
    view: PlayersView;
    position?: string;
  } = {
    statCategory: '',
    yearAggregate: true,
    view: 'leaders',
  };

  if (segments[0] !== 'players') {
    return result;
  }

  if (segments.length === 1) {
    return result;
  }

  const second = segments[1];
  if (second === 'rookies') {
    const year = parseInt(segments[2], 10);
    if (!isNaN(year) && year >= 2010 && year <= 2100) {
      result.year = year;
      result.view = segments[3] === 'by-college' ? 'rookies-by-college' : 'rookies';
    }
    return result;
  }

  const secondAsYear = parseInt(second, 10);

  if (isValidStatCategory(second) && second !== '') {
    result.statCategory = second as StatCategory;
    if (segments.length >= 3) {
      const third = segments[2];
      const year = parseInt(third, 10);
      if (!isNaN(year) && year >= 2010 && year <= 2100) {
        result.year = year;
        if (segments.length >= 5 && segments[3] === 'week') {
          const week = parseInt(segments[4], 10);
          if (!isNaN(week) && week >= 1 && week <= 25) {
            result.week = week;
            result.yearAggregate = false;
          }
        }
      } else if (segments.length === 3 && isPlayersWave2PositionToken(third)) {
        result.position = third.toLowerCase();
      }
    }
  } else if (!isNaN(secondAsYear) && secondAsYear >= 2010 && secondAsYear <= 2100) {
    result.year = secondAsYear;
    if (segments.length >= 4 && segments[2] === 'week') {
      const week = parseInt(segments[3], 10);
      if (!isNaN(week) && week >= 1 && week <= 25) {
        result.week = week;
        result.yearAggregate = false;
      }
    }
  }

  return result;
};

/**
 * True only when pathname is an exact player-leaderboard route (query ignored).
 * Unknown paths like /players/foo or /players/receiving/xx are invalid so the gate 404s them.
 */
export const isValidPlayersPath = (pathname: string): boolean => {
  const path = (pathname || '').split('?')[0].replace(/\/$/, '') || '/players';
  if (path === '/players') return true;
  if (!path.startsWith('/players/')) return false;
  const career = parsePlayerCareerPath(path);
  if (career) return getPlayerCareerPath(career.stat) === path;
  const parsed = parsePlayersUrl(path);
  if (parsed.view === 'rookies' || parsed.view === 'rookies-by-college') {
    return parsed.year != null && getRookiesUrl(parsed.year, parsed.view === 'rookies-by-college') === path;
  }
  if (parsed.position) {
    if (
      !matchesPlayersWave2Position({
        statCategory: parsed.statCategory,
        position: parsed.position,
      })
    ) {
      return false;
    }
  }
  return (
    getPlayersUrl(
      parsed.statCategory,
      parsed.year,
      parsed.week,
      parsed.yearAggregate,
      parsed.position || null
    ) === path
  );
};

/**
 * Generates full URL with base site URL
 * @param path - URL path (e.g., '/rankings/passing')
 * @returns Full URL (e.g., 'https://collegiatenflimpact.com/rankings/passing')
 */
export const getFullUrl = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${BASE_SITE_URL}${cleanPath}`;
};
